import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import toCurrency from '../../util/toCurrency';
import amortizationCalculator from '../../util/calculators/amortizationCalculator';
import Calculator from '../../components/Calculator/new';
import Slider from '../../components/Shared/Slider';
import Heading from '../../components/Heading/Heading';
import AmortizationSchedule from '../../components/AmortizationSchedule/AmortizationSchedule';
import Results from '../../components/Shared/CalculatorResults/Card';
import theme from '../../styles/theme';
import CTASimple from '../../components/CTASimple/CTASimple';
import { getPageContent } from '../../services/dpc';
import Error from '../../components/Error/Error';
import useGTM from '../../hooks/useGTM';
import cleanNumber from '../../util/cleanNumber';
import createGTMClass from '../../util/createGTMClass';

const eventLabels = {
  loanAmount: 'borrow_amount',
  interestRate: 'interest_rate',
  loanTerm: 'loan_term',
};

const defaultErrors = {
  loanAmount: false,
  interestRate: false,
  currentPayment: false,
  additionalPayment: false,
};

const schema = yup.object().shape({
  loanAmount: yup
    .number()
    .typeError('How much are you looking to borrow is required')
    .transform((o, v) => cleanNumber(v))
    .min(1, 'How much are you looking to borrow must be no less than $1')
    .max(500000, 'How much are you looking to borrow must not exceed $500,000'),
  interestRate: yup
    .number()
    .typeError('Interest rate is required')
    .transform((o, v) => cleanNumber(v))
    .min(3.5, 'Interest rate must be no less than 3.5%')
    .max(25, 'Interest rate must not exceed 25%'),
  loanTerm: yup
    .number()
    .typeError('Term of loan in years is required')
    .transform((o, v) => cleanNumber(v))
    .min(1, 'Term of loan must be no less than 1 year')
    .max(30, 'Term of loan must not exceed 30 years'),
});

const updateValue =
  (values, setValue, events, setEvents, triggerGTMEvent) =>
  ({ currentTarget }) => {
    const { type, name } = currentTarget;
    const hasDecimal = currentTarget.value.indexOf('.') > -1;
    const [value, decimal = ''] = currentTarget.value
      .replace(/[^0-9.]/g, '')
      .split('.');
    const formattedString = `${value.split(/(?=(?:\d{3})+(?:\.|$))/g).join(',')}${
      hasDecimal ? '.' : ''
    }${decimal.slice(0, 2)}`;

    setValue({
      ...values,
      [name]: formattedString,
    });

    if (events[name][type]) {
      clearTimeout(events[name][type]);
    }

    setEvents({
      ...events,
      [name]: {
        ...events[name],
        [type]: setTimeout(() => {
          triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Amortization Calculator',
            category: 'loan_evaluation',
            action: type === 'range' ? 'slider' : 'text',
            label: `${eventLabels[name]}_${
              type === 'range' ? 'slider' : 'text_input'
            }`,
          })();

          setEvents({
            ...events,
            [name]: {
              ...events[name],
              [type]: false,
            },
          });
        }, 500),
      },
    });
  };

function AmortizationCalculator({
  data: {
    contentCTAs,
    contentVideos,
    faqPod,
    heroCta,
    heroMedia,
    heroText,
    mobileHeroMedia,
    metaDescription,
    preFooterCta,
    tipsAndToolsPod,
    titleTag,
    customerReviewPod,
    pageTitle,
    ...props
  } = {},
  error,
  isLoading,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();

  const [errors, setErrors] = useState(defaultErrors);
  const [values, setValues] = useState({
    loanAmount: '99,564.68',
    interestRate: '6.75',
    loanTerm: '25',
    startDate: new Date(),
    additionalPrincipalPayment: '0',
  });
  const [results, setResults] = useState({
    error: null,
    amortization: null,
    paymentAmount: null,
  });
  const [events, setEvents] = useState({
    loanAmount: {
      text: false,
      range: false,
    },
    interestRate: {
      text: false,
      range: false,
    },
    loanTerm: {
      text: false,
      range: false,
    },
  });

  useEffect(() => {
    schema
      .validate(values, { abortEarly: false })
      .then(() => {
        const amortization = amortizationCalculator(
          cleanNumber(values.loanAmount),
          cleanNumber(values.interestRate),
          cleanNumber(values.loanTerm),
          values.startDate,
          cleanNumber(values.additionalPrincipalPayment)
        );
        const { paymentAmount } = amortization[0];

        setResults({
          amortization,
          paymentAmount,
        });
        setErrors(defaultErrors);
      })
      .catch(({ inner }) => {
        const newErrors = inner.reduce(
          (errors, { path, message }) => ({
            ...errors,
            [path]: message,
          }),
          {}
        );

        setErrors({
          ...defaultErrors,
          ...newErrors,
        });
        setResults({
          error: 'Please fix the errors to see your results',
        });
      });
  }, [values]);

  return (
    <Layout isLoading={isLoading}>
      <SEO title={titleTag} description={metaDescription} />

      <Heading appearance='dark' background={theme.colors.blue10}>
        <h1>{pageTitle}</h1>
        <ReactMarkdown
          source={heroText}
          renderers={{
            paragraph: ({ children }) => (
              <p className='type-caption'>{children}</p>
            ),
          }}
        />
      </Heading>

      <Calculator
        fields={
          <>
            <Slider
              label='How much are you looking to borrow?'
              name='loanAmount'
              min={1}
              max={500000}
              value={values.loanAmount}
              onChange={updateValue(
                values,
                setValues,
                events,
                setEvents,
                triggerGTMEvent
              )}
              prefix='$'
              error={errors.loanAmount}
            />
            <Slider
              label='Interest rate'
              name='interestRate'
              min={3.5}
              max={25}
              step={0.25}
              value={values.interestRate}
              onChange={updateValue(
                values,
                setValues,
                events,
                setEvents,
                triggerGTMEvent
              )}
              suffix='%'
              error={errors.interestRate}
              tooltip='Interest Rate - The percentage cost of the principal borrowed.'
            />
            <Slider
              label='Term of loan in years'
              name='loanTerm'
              min={1}
              max={30}
              value={values.loanTerm}
              error={errors.loanTerm}
              onChange={updateValue(
                values,
                setValues,
                events,
                setEvents,
                triggerGTMEvent
              )}
              suffix='yrs'
            />
          </>
        }
        results={
          <Results
            heading={
              !results.error ? (
                <>
                  <h2 className='no-margin-top margin-bottom-8'>
                    Your monthly payment
                  </h2>
                  <h3 className='type-h1 color-claytonBlue no-margin-top margin-bottom-2'>
                    {toCurrency(results.paymentAmount || 0)}
                  </h3>
                  <p className='type-caption no-margin-top no-margin-bottom'>
                    View full amortization schedule below
                  </p>
                </>
              ) : (
                <h2 className='type-h4'>{results.error}</h2>
              )
            }
          />
        }
        content={
          !results.error
            ? results.amortization && (
                <AmortizationSchedule results={results.amortization} />
              )
            : null
        }
        disclaimer={
          <p className='type-xs'>
            <u>Please note:</u> The results from the calculator are only
            estimates. There are additional costs to buying a home that may not
            be reflected in this calculator. Always compare loan offers you may
            receive before making your decision. Contact us to talk to a
            licensed loan specialist about loan options specific to your
            financial needs.{' '}
          </p>
        }
      />

      <CTASimple
        title={preFooterCta?.ctaHeader || 'Find the Perfect Home Loan for You'}
        link={{
          to: preFooterCta?.firstCTAButtonUrl || '/applynow/welcome/',
          title: preFooterCta?.firstCTAButtonText || 'Apply Today',
          isExternal: true,
          newTab: true,
          size: 'large',
          className: createGTMClass('apply', 'prefoot_cta', 'button'),
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Amortization Calculator',
            category: 'loan_application',
            action: 'button',
            label: 'apply',
          }),
        }}
        background={theme.colors.claytonBlue}
      >
        <ReactMarkdown source={preFooterCta?.ctaIntro} />
      </CTASimple>
    </Layout>
  );
}

AmortizationCalculator.getInitialProps = async ({}) => {
  try {
    const data = await getPageContent('/calculators/amortization-calculator/');

    if (data.status !== 200) {
      throw data.statusText;
    }

    return {
      data: data.data.result,
    };
  } catch (error) {
    return {
      error,
    };
  }
};

AmortizationCalculator.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default AmortizationCalculator;
